/**
 * Top Progress Bar Component
 *
 * Provides instant visual feedback during navigation.
 * Inspired by YouTube, GitHub, Linear (Meta standard).
 *
 * Design Goals:
 * - Visible within 16ms of navigation start
 * - Smooth animation (60fps)
 * - Minimal performance overhead
 * - Works on all themes
 *
 * @module TopProgressBar
 */

import { useRouteLoading } from '../../contexts/RouteLoadingContext'
import './TopProgressBar.css'

/**
 * Top Progress Bar
 *
 * A thin bar at the top of the viewport that shows navigation progress.
 *
 * Visual Design:
 * - 2px height (subtle, non-intrusive)
 * - Primary brand color (consistent with app theme)
 * - Smooth animation (CSS transitions)
 * - Glow effect (Meta polish)
 *
 * Performance:
 * - Uses CSS transform (GPU accelerated)
 * - No layout thrashing (fixed position)
 * - Minimal re-renders (only on progress change)
 *
 * @example
 * ```tsx
 * <AppLayout>
 *   <TopProgressBar />
 *   <Routes>...</Routes>
 * </AppLayout>
 * ```
 */
export function TopProgressBar() {
  const { isNavigating, progress } = useRouteLoading()

  // Don't render if not navigating (reduces DOM nodes)
  if (!isNavigating) {
    return null
  }

  return (
    <div
      className="top-progress-bar"
      style={{
        width: `${progress}%`,
      }}
      role="progressbar"
      aria-label="Page loading progress"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
    />
  )
}

/**
 * Accessibility Notes:
 *
 * - role="progressbar": Announces to screen readers
 * - aria-valuenow: Current progress value
 * - aria-label: Describes what's loading
 *
 * Screen reader behavior:
 * - Announces "Page loading progress, 45%" as bar progresses
 * - User understands something is happening
 * - Improves accessibility for vision-impaired users
 */

/**
 * Performance Notes:
 *
 * 1. Why use width instead of scaleX?
 *    - scaleX is more performant (GPU), but causes visual issues with glow
 *    - width with GPU acceleration (will-change) is fast enough
 *    - Smoother visual result
 *
 * 2. Why inline style?
 *    - Progress changes frequently (every 100ms)
 *    - Inline style avoids class recalculation
 *    - Direct DOM update is fastest
 *
 * 3. Why conditional render?
 *    - Removes element from DOM when not needed
 *    - Reduces memory footprint
 *    - Cleaner DOM tree for debugging
 */
